import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import type { FleetSnapshot, FleetTech } from '@/utils/fleetStatus';

export const FLEET_KEY = ['fleet', 'live'] as const;

const REALTIME_OFF = String(import.meta.env.VITE_REALTIME_MODE ?? '').trim() === 'off';

/** tech_report_locations broadcast: phone status always, position only when it moved. */
type FleetBroadcast = Partial<FleetTech> & { tech_id: string };

/**
 * Every active technician with phone status and (inside tracking hours) their newest position,
 * from get_fleet_live() (migration 20261019130000; admins and CSRs only).
 *
 * With `realtime`, it also joins the private 'fleet:<tenant>' topic and patches the cache from
 * each tech_location broadcast, so markers move without a refetch. That topic is separate from
 * 'tenant:<id>' because techs can read the tenant topic and must never see each other. Polls
 * every minute while the topic is not joined; every 5 minutes otherwise as a safety net.
 */
export function useFleetLive({ enabled = true, realtime = true }: { enabled?: boolean; realtime?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [joined, setJoined] = useState(false);

  const query = useQuery({
    queryKey: FLEET_KEY,
    enabled: enabled && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_fleet_live');
      if (error) throw error;
      return data as unknown as FleetSnapshot;
    },
    refetchInterval: joined ? 5 * 60_000 : 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!enabled || !realtime || !tenantId || REALTIME_OFF) return;

    const channel = supabase.channel(`fleet:${tenantId}`, { config: { private: true } });
    channel.on('broadcast', { event: 'tech_location' }, ({ payload }) => {
      const msg = payload as FleetBroadcast | undefined;
      if (!msg?.tech_id) return;
      let known = false;
      queryClient.setQueryData<FleetSnapshot>(FLEET_KEY, (old) => {
        if (!old) return old;
        return {
          ...old,
          techs: old.techs.map((t) => {
            if (t.tech_id !== msg.tech_id) return t;
            known = true;
            return { ...t, ...msg };
          }),
        };
      });
      if (!known) void queryClient.invalidateQueries({ queryKey: FLEET_KEY });
    });
    channel.subscribe((status) => setJoined(status === 'SUBSCRIBED'));

    return () => {
      setJoined(false);
      void supabase.removeChannel(channel);
    };
  }, [enabled, realtime, tenantId, queryClient]);

  return { ...query, live: joined };
}

export interface TrailPoint {
  lat: number;
  lng: number;
  accuracy_m?: number | null;
  speed_mps?: number | null;
  heading?: number | null;
  source?: string | null;
  job_id?: string | null;
  recorded_at: string;
}

/** One day's trail for a technician (company timezone). Admin-only server-side; each view is audited. */
export function useTechTrail(techId: string | null, day: string, enabled: boolean) {
  return useQuery({
    queryKey: ['fleet', 'trail', techId, day],
    enabled: enabled && !!techId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_tech_trail', { p_tech_id: techId as string, p_day: day });
      if (error) throw error;
      return (data ?? []) as unknown as TrailPoint[];
    },
    refetchInterval: 2 * 60_000,
  });
}
